/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import type { BillingCycle } from '@/features/onboarding/hooks/use-plans';

/** One line of the subscription as it stands: a plan and how many seats of it. */
export interface SubscriptionLine {
  plan_id: string;
  quantity: number;
}

/** What a line becomes once the change is applied. */
export interface ChangeLine {
  plan_id: string;
  seats: number;
}

/**
 * Seats the subscription is paying for, across every plan on it.
 *
 * @param lines - The subscription's lines.
 * @returns The total seat count, zero when there are none.
 */
export function totalSeats(lines: readonly SubscriptionLine[] | undefined): number {
  return (lines ?? []).reduce((total, line) => total + (line.quantity ?? 0), 0);
}

/**
 * Whether the team is spread across more than one plan.
 *
 * A mixed team cannot be moved to another tier as one unit — which seats
 * would move? — so the plan grid stands down for it and only the cycle
 * change is offered. Seat composition belongs to the team screen, which
 * already asks per plan.
 *
 * @param lines - The subscription's lines.
 * @returns True when two or more plans carry seats.
 */
export function isMixedTeam(lines: readonly SubscriptionLine[] | undefined): boolean {
  return (lines ?? []).filter((line) => (line.quantity ?? 0) > 0).length > 1;
}

/**
 * Whether this subscription has to be changed through its lines rather than
 * as a single plan.
 *
 * One seat is the solo broker's shape, and `change-plan` says everything
 * there is to say about it. Anything above that is a team, and a request
 * naming one plan and one price would describe a subscription the agency
 * does not have.
 *
 * @param lines - The subscription's lines.
 * @returns True when the change must carry seat counts.
 */
export function needsSeatAwareChange(lines: readonly SubscriptionLine[] | undefined): boolean {
  return totalSeats(lines) > 1 || isMixedTeam(lines);
}

/**
 * The lines the subscription should hold after the change.
 *
 * Seat counts are carried over untouched: this flow changes what a seat is
 * priced as, never how many there are. A mixed team keeps each of its plans
 * and only moves cycle; a team on one plan moves to whatever tier was
 * chosen, with the same number of seats.
 *
 * @param lines - The subscription's current lines.
 * @param cycle - The cycle the change is heading to.
 * @param targetPlanId - The chosen tier's plan id in that cycle, when a tier
 *   was chosen; a mixed team ignores it.
 * @param planIdFor - Resolves a current plan id to its id in the target cycle.
 * @returns The lines to send, or null when nothing can be resolved.
 */
export function linesForChange(
  lines: readonly SubscriptionLine[] | undefined,
  cycle: BillingCycle,
  targetPlanId: string | null,
  planIdFor: (planId: string, cycle: BillingCycle) => string | null,
): ChangeLine[] | null {
  const held = (lines ?? []).filter((line) => (line.quantity ?? 0) > 0);
  if (held.length === 0) return null;

  if (isMixedTeam(held)) {
    const mapped = held.map((line) => ({
      plan_id: planIdFor(line.plan_id, cycle) ?? line.plan_id,
      seats: line.quantity,
    }));
    return mapped;
  }

  const planId = targetPlanId ?? planIdFor(held[0].plan_id, cycle);
  if (!planId) return null;
  return [{ plan_id: planId, seats: totalSeats(held) }];
}

/**
 * What the chosen lines cost per charge, from a per-seat price.
 *
 * The catalogue prices a seat; a team pays for as many as it holds, and the
 * review used to print the seat's price as the team's bill.
 *
 * @param seatPriceCents - What one seat costs in the chosen cycle.
 * @param seats - How many seats the subscription holds.
 * @returns The total in cents, or null when the price is unknown.
 */
export function totalForSeats(
  seatPriceCents: number | null | undefined,
  seats: number,
): number | null {
  if (seatPriceCents === null || seatPriceCents === undefined) return null;
  return seatPriceCents * Math.max(seats, 1);
}

/** What the review needs from whichever preview answered. */
export interface PreviewFacts {
  proRataCents: number | null;
  daysRemaining: number | null;
  /** Free period only: what the new plan costs when the period ends. */
  nextChargeCents: number | null;
  /** The change cancels a user reduction scheduled for the next renewal. */
  seatReductionCleared: boolean;
  failed: boolean;
}

/**
 * Picks the figures from the preview that applies to this subscription.
 *
 * A team is previewed through its lines and a single seat through its plan;
 * the two endpoints answer in different shapes, and the review only wants
 * the three facts they agree on.
 *
 * @param seatAware - Whether the change goes through the subscription lines.
 * @param seat - The lines preview, when there is one.
 * @param plan - The plan preview, when there is one.
 * @returns The figures to show.
 */
export function previewFacts(
  seatAware: boolean,
  seat: {
    charged_today_cents?: number;
    next_charge_amount_cents?: number;
    days_remaining?: number;
    scheduled_seat_change_cleared?: boolean;
    failed: boolean;
  } | null,
  plan: {
    pro_rata_amount_cents?: number;
    next_charge_amount_cents?: number;
    days_remaining?: number;
    scheduled_seat_change_cleared?: boolean;
    failed: boolean;
  } | null,
): PreviewFacts {
  if (seatAware) {
    return {
      proRataCents: seat?.charged_today_cents ?? null,
      daysRemaining: seat?.days_remaining ?? null,
      nextChargeCents: seat?.next_charge_amount_cents ?? null,
      seatReductionCleared: seat?.scheduled_seat_change_cleared === true,
      failed: Boolean(seat?.failed),
    };
  }
  return {
    proRataCents: plan?.pro_rata_amount_cents ?? null,
    daysRemaining: plan?.days_remaining ?? null,
    nextChargeCents: plan?.next_charge_amount_cents ?? null,
    seatReductionCleared: plan?.scheduled_seat_change_cleared === true,
    failed: Boolean(plan?.failed),
  };
}
